import * as React from 'react';

import { ConfirmDialog, type ConfirmDialogOptions } from './confirm-dialog';

interface PendingConfirmation {
  options: ConfirmDialogOptions;
}

export interface ConfirmationDialogController {
  requestConfirmation: (options: ConfirmDialogOptions) => Promise<boolean>;
  confirmationDialog: React.ReactElement;
}

/**
 * Local, promise-based confirmation controller.
 *
 * Keeping the host beside the calling feature avoids a global imperative modal
 * service while giving every surface the same accessible confirmation UI.
 */
export function useConfirmationDialog(): ConfirmationDialogController {
  const [pendingConfirmation, setPendingConfirmation] = React.useState<PendingConfirmation | null>(null);
  const resolverRef = React.useRef<((confirmed: boolean) => void) | null>(null);

  const settle = React.useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setPendingConfirmation(null);
  }, []);

  const requestConfirmation = React.useCallback((options: ConfirmDialogOptions) => {
    resolverRef.current?.(false);

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setPendingConfirmation({ options });
    });
  }, []);

  React.useEffect(() => () => {
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  return {
    requestConfirmation,
    confirmationDialog: (
      <ConfirmDialog
        open={pendingConfirmation !== null}
        title={pendingConfirmation?.options.title ?? ''}
        description={pendingConfirmation?.options.description ?? ''}
        confirmLabel={pendingConfirmation?.options.confirmLabel}
        cancelLabel={pendingConfirmation?.options.cancelLabel}
        variant={pendingConfirmation?.options.variant}
        onConfirm={() => settle(true)}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      />
    ),
  };
}
